import React from "react";
import { motion } from "framer-motion";

const getVariants = (darkMode) => ({
  // Primary CTA — cohesive accent gradient
  primary: darkMode
    ? "bg-accent-gradient text-[#04121F] font-semibold shadow-glow-sm hover:shadow-glow-md border border-white/10"
    : "bg-sky-200 text-sky-800 hover:bg-sky-300 shadow-sm border border-sky-400 font-semibold",

  secondary: darkMode 
    ? "glass text-white hover:hairline-strong hover:bg-white/[0.04]"
    : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50",

  outline: darkMode
    ? "bg-white/[0.03] border border-white/15 hover:bg-white/[0.06] text-slate-100"
    : "bg-transparent border border-slate-200 text-slate-600 hover:bg-slate-50",

  ghost: darkMode
    ? "text-slate-400 hover:text-white hover:bg-white/[0.06] rounded-lg"
    : "text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg",

  danger: "bg-rose-500/90 hover:bg-rose-500 text-white shadow-e1",

  icon: darkMode
    ? "text-slate-400 hover:text-white hover:bg-white/[0.07] rounded-xl border border-transparent hover:border-white/10"
    : "text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl border border-transparent",

  // Round send button
  fab: darkMode
    ? "rounded-full bg-accent-gradient text-[#04121F] shadow-glow-md hover:shadow-glow-lg"
    : "rounded-full bg-sky-300 text-sky-900 shadow-sm hover:bg-sky-400",

  // Suggestion chips on the hero
  chip: darkMode
    ? "rounded-2xl glass text-slate-100 hover:bg-accent-400/[0.08] hover:hairline-strong hover:shadow-glow-sm text-left"
    : "rounded-2xl bg-white border border-slate-200 text-slate-700 hover:border-blue-200 hover:bg-blue-50 text-left shadow-sm",

  // Google sign-in — must remain white per Google brand guidelines
  google:
    "w-full bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 py-3 px-4 rounded-2xl flex items-center justify-center gap-3 font-semibold shadow-sm disabled:opacity-50",
});

const sizes = {
  xs: "px-2 py-1 text-xs",
  sm: "px-3 py-2 text-sm",
  md: "px-4 py-2.5 text-sm",
  lg: "px-5 py-3 text-base",
  icon: "w-10 h-10",
  fab: "w-12 h-12",
};

const Button = ({
  children,
  variant = "primary",
  size = "md",
  loading = false,
  disabled = false,
  icon,
  loadingText,
  className = "",
  darkMode = true,
  ...props
}) => {
  const isDisabled = disabled || loading;
  const currentVariants = getVariants(darkMode);

  return (
    <motion.button
      disabled={isDisabled}
      whileHover={isDisabled ? undefined : { y: -1 }}
      whileTap={isDisabled ? undefined : { scale: 0.97 }}
      transition={{ type: "spring", stiffness: 400, damping: 26 }}
      className={`
        relative inline-flex items-center justify-center gap-2
        font-medium
        transition-[background,box-shadow,color,border-color] duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
        focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/50
        ${currentVariants[variant] || ""}
        ${sizes[size] || ""}
        ${className}
      `}
      {...props}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-current/40 border-t-current rounded-full animate-spin" />
      ) : (
        icon
      )}
      {loading && loadingText ? loadingText : children}
    </motion.button>
  );
};

export default Button;
